import React, { useEffect, useState } from 'react';
import MsgReader from '@kenjiuno/msgreader';

const MsgViewer = ({ fileUrl }) => {
  const [msgData, setMsgData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [htmlBody, setHtmlBody] = useState(null);
  
  // Clean null characters from strings
  const cleanString = (str) => {
    if (!str) return '';
    return str.replace(/\u0000/g, '').trim();
  };

  // Extract body from raw headers
  const extractBodyFromHeaders = (headers) => {
    if (!headers) return '';
    
    // Look for the first empty line that separates headers from body
    const headerEnd = headers.indexOf('\r\n\r\n');
    if (headerEnd === -1) return '';
    
    return headers.slice(headerEnd + 4); // Skip the 4 newline characters
  };

  // Extract date from headers
  const extractDateFromHeaders = (headers) => {
    if (!headers) return null;
    
    const dateMatch = headers.match(/Date: (.*)\r\n/);
    return dateMatch ? dateMatch[1] : null;
  };

  useEffect(() => {
    const processMsgFile = async () => {
      try {
        const response = await fetch(fileUrl);
        const arrayBuffer = await response.arrayBuffer();
        const msgReader = new MsgReader(arrayBuffer);
        const parsed = msgReader.getFileData();
        
        // Process HTML content if available
        if (parsed.html) {
          const decoder = new TextDecoder('utf-8');
          const decodedString = decoder.decode(parsed.html);
          setHtmlBody(decodedString);
        }
        
        // Manually extract body content
        const rawContent = extractBodyFromHeaders(parsed.headers);
        const bodyContent = parsed.body || rawContent || '';
        const dateStr = extractDateFromHeaders(parsed.headers);
        
        setMsgData({
          sender: cleanString(parsed.senderName) + (parsed.senderEmail ? ` <${cleanString(parsed.senderEmail)}>` : ''),
          recipients: parsed.recipients.map(r => 
            `${cleanString(r.name)} <${cleanString(r.email)}>`
          ),
          subject: cleanString(parsed.subject),
          date: dateStr ? new Date(dateStr) : null,
          body: bodyContent,
          attachments: parsed.attachments.map((att, i) => {
            const file = msgReader.getAttachment(i);
            return {
              name: cleanString(att.fileName),
              url: URL.createObjectURL(new Blob([file.content]))
            };
          })
        });
      } catch (err) {
        console.error('MSG processing error:', err);
        setError(`Error processing MSG file: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    if (fileUrl) {
      processMsgFile();
    }
  }, [fileUrl]);

  // Cleanup function
  useEffect(() => {
    return () => {
      if (msgData?.attachments) {
        msgData.attachments.forEach(att => URL.revokeObjectURL(att.url));
      }
    };
  }, [msgData]);

  return (
    <div className="msg-viewer">
      {loading && <div className="loading-indicator">Loading message...</div>}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {msgData && !loading && (
        <div className="email-container">
          <div className="email-header">
            <h2>{msgData.subject}</h2>
            <div className="email-metadata">
              <div><strong>From:</strong> {msgData.sender}</div>
              <div><strong>To:</strong> {msgData.recipients.join(', ')}</div>
              {msgData.date && (
                <div><strong>Date:</strong> {msgData.date.toLocaleString()}</div>
              )}
            </div>
            
            {msgData.attachments.length > 0 && (
              <div className="attachments">
                <h3>Attachments</h3>
                <ul>
                  {msgData.attachments.map((att, index) => (
                    <li key={index}>
                      <a href={att.url} download={att.name}>
                        {att.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <div className="email-content">
            {htmlBody ? (
              <div dangerouslySetInnerHTML={{ __html: htmlBody }} />
            ) : (
              <pre className="plain-text-body">{msgData.body}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MsgViewer;